// The billing file: preview a period per customer, create and download the file, earlier runs.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sv } from '@aldorock-fordon/shared/sv';
import { api } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { TextField } from '../ui/Fields.tsx';
import { Table } from '../ui/Table.tsx';
import { kr, moment, todayIso } from '../format.ts';

type Customer = {
  customer: string;
  bilpool: number;
  fordon: number;
  verkstad: number;
  total: number;
  lines: number;
};
type Preview = {
  perCustomer: Customer[];
  total: number;
  lines: number;
  sample: Array<{
    customer: string;
    kind: string;
    date: string;
    reference: string;
    text: string;
    amountSek: number;
  }>;
};
type Run = {
  id: string;
  periodFrom: string;
  periodTo: string;
  status: string;
  fileName: string;
  totalSek: number;
  createdAt: string;
  lines: Customer[];
};

const monthStart = () => `${todayIso().slice(0, 7)}-01`;
const lastMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  const from = d.toISOString().slice(0, 10);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { from, to: `${from.slice(0, 7)}-${String(end.getDate()).padStart(2, '0')}` };
};

export function BillingPage() {
  const h1 = usePage(sv.billing.title);
  const qc = useQueryClient();
  const [from, setFrom] = useState(lastMonth().from);
  const [to, setTo] = useState(lastMonth().to);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const runs = useQuery({ queryKey: ['billing-runs'], queryFn: () => api<Run[]>('/billing/runs') });
  const load = useMutation({
    mutationFn: () => api<Preview>(`/billing/preview?from=${from}&to=${to}`),
    onSuccess: (p) => {
      setPreview(p);
      setMessage(null);
    },
  });
  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/billing/runs', { method: 'POST', body: JSON.stringify({ from, to }) }),
    onSuccess: async () => {
      setMessage(sv.billing.created);
      await qc.invalidateQueries({ queryKey: ['billing-runs'] });
    },
  });
  void monthStart;
  return (
    <>
      <PageHead
        help="billing"
        crumbs={[{ to: '/admin', label: sv.adminHome.title }, { label: sv.billing.title }]}
        title={sv.billing.title}
        titleRef={h1}
      />
      <p>{sv.billing.intro}</p>
      <p className="caption">{sv.billing.sftpNote}</p>
      <form
        className="form-block"
        onSubmit={(e) => {
          e.preventDefault();
          load.mutate();
        }}
      >
        <div className="cols cols-2 tight">
          <TextField
            id="from"
            label={sv.reports.from}
            type="date"
            value={from}
            required
            onChange={setFrom}
          />
          <TextField
            id="to"
            label={sv.reports.to}
            type="date"
            value={to}
            required
            onChange={setTo}
          />
        </div>
        <div className="form-actions">
          <button
            className="btn btn-primary"
            type="submit"
            disabled={load.isPending}
            aria-busy={load.isPending}
          >
            {sv.billing.preview}
          </button>
        </div>
      </form>
      {message ? (
        <p className="notice" data-state="done" role="status">
          {message}
        </p>
      ) : null}
      {preview ? (
        <section className="block" aria-labelledby="underlag">
          <h2 className="block-title" id="underlag">
            {sv.billing.perCustomer}
          </h2>
          {preview.perCustomer.length === 0 ? <p className="muted">{sv.billing.empty}</p> : null}
          <Table
            caption={sv.billing.perCustomer}
            isEmpty={preview.perCustomer.length === 0}
            empty={sv.billing.empty}
            hasOptional
          >
            <thead>
              <tr>
                <th scope="col">{sv.billing.customer}</th>
                <th scope="col" className="r col-opt">
                  {sv.billing.bilpool}
                </th>
                <th scope="col" className="r col-opt">
                  {sv.billing.fordon}
                </th>
                <th scope="col" className="r col-opt">
                  {sv.billing.verkstad}
                </th>
                <th scope="col" className="r">
                  {sv.billing.total}
                </th>
                <th scope="col" className="r col-opt">
                  {sv.billing.lines}
                </th>
              </tr>
            </thead>
            <tbody>
              {preview.perCustomer.map((c) => (
                <tr key={c.customer}>
                  <td>{c.customer}</td>
                  <td className="r col-opt">{kr(c.bilpool)}</td>
                  <td className="r col-opt">{kr(c.fordon)}</td>
                  <td className="r col-opt">{kr(c.verkstad)}</td>
                  <td className="r">{kr(c.total)}</td>
                  <td className="r col-opt">{c.lines}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>{sv.billing.total}</td>
                <td className="col-opt" />
                <td className="col-opt" />
                <td className="col-opt" />
                <td className="r" data-testid="billing-total">
                  {kr(preview.total)}
                </td>
                <td className="r col-opt">{preview.lines}</td>
              </tr>
            </tfoot>
          </Table>
          <div className="form-actions">
            <button
              className="btn btn-primary"
              type="button"
              disabled={create.isPending || preview.lines === 0}
              aria-busy={create.isPending}
              onClick={() => create.mutate()}
            >
              {sv.billing.create}
            </button>
          </div>
        </section>
      ) : null}
      <Table caption={sv.billing.runs} isEmpty={(runs.data?.length ?? 0) === 0} hasOptional>
        <thead>
          <tr>
            <th scope="col">{sv.columns.date}</th>
            <th scope="col">{sv.billing.period}</th>
            <th scope="col" className="r">
              {sv.billing.total}
            </th>
            <th scope="col" className="col-opt">
              {sv.columns.status}
            </th>
            <th scope="col">{sv.columns.action}</th>
          </tr>
        </thead>
        <tbody>
          {runs.data?.map((r) => (
            <tr key={r.id}>
              <td className="num">{moment(r.createdAt)}</td>
              <td className="num">{sv.range(r.periodFrom, r.periodTo)}</td>
              <td className="r">{kr(r.totalSek)}</td>
              <td className="col-opt">{sv.billing.status[r.status] ?? r.status}</td>
              <td>
                <a href={`/api/v1/billing/runs/${r.id}/file`} download={r.fileName}>
                  {sv.billing.download}
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}
